/**
 * Validates path+query values for post-auth return (`redirect_url` on /sign-in, /sign-up).
 * Prevents open redirects; restricts query keys; drops expired auth intents.
 */

import { EVENT_NAV_FROM_PARAM } from "@/lib/events/event-nav-from"

const DEBUG_FLAG = "NEXT_PUBLIC_DEBUG_MAATII_AUTH_RETURN"

export const AUTH_RETURN_SESSION_KEY = "maatii:auth-return-path:v1" as const

const INTENT_MAX_AGE_SEC = 15 * 60
/** Max age for sessionStorage return path (sign-in ↔ sign-up handoff). */
const SESSION_RETURN_MAX_AGE_MS = 15 * 60 * 1000

const ALLOWED_PATH_PREFIXES = [
  /^\/event\/[^/?#\s]+$/,
  /^\/invite\/[^/?#\s]+$/,
  /^\/workspace-invite\/[^/?#\s]+$/,
  /^\/event-manager-invite\/[^/?#\s]+$/,
] as const

const DISALLOWED_PATH_SUBSTRINGS = [
  "/sign-in",
  "/sign-up",
  "//",
  "\\\\",
  "..",
  "%2f%2f",
  "%5c",
] as const

const ALLOWED_QUERY_KEYS = new Set([
  "share",
  "save_timeline",
  "resume_upload",
  "auth_intent_ts",
  "tab",
  "media",
  EVENT_NAV_FROM_PARAM,
])

function debugLogAuthReturn(message: string, detail?: Record<string, unknown>) {
  if (process.env[DEBUG_FLAG] !== "1") return
  if (detail) {
    console.info(`[maatii:auth-return] ${message}`, detail)
  } else {
    console.info(`[maatii:auth-return] ${message}`)
  }
}

function isSafePathname(pathname: string): boolean {
  const p = pathname.trim()
  if (!p.startsWith("/")) return false
  for (const bad of DISALLOWED_PATH_SUBSTRINGS) {
    if (p.toLowerCase().includes(bad)) return false
  }
  return ALLOWED_PATH_PREFIXES.some((re) => re.test(p))
}

function validateQueryValue(key: string, value: string): boolean {
  const v = value.trim()
  if (v.length > 512) return false
  switch (key) {
    case "share":
      return /^[\w.-]{4,256}$/.test(v)
    case "save_timeline":
    case "resume_upload":
      return v === "1" || v.toLowerCase() === "true"
    case "auth_intent_ts": {
      if (!/^\d{1,12}$/.test(v)) return false
      const sec = Number(v)
      if (!Number.isFinite(sec)) return false
      const now = Math.floor(Date.now() / 1000)
      return now - sec <= INTENT_MAX_AGE_SEC
    }
    case "tab": {
      const t = v.toLowerCase()
      return t === "photos" || t === "guests" || t === "details"
    }
    case "media":
      return /^[\w-]{1,80}$/.test(v)
    case EVENT_NAV_FROM_PARAM: {
      const t = v.toLowerCase()
      return t === "events" || t === "attending" || t === "timeline"
    }
    default:
      return false
  }
}

/**
 * Returns a safe path beginning with `/`, including validated query string, or null.
 * Expired `auth_intent_ts` causes `save_timeline` / `resume_upload` to be stripped; if the
 * path is still valid, that sanitized path is returned.
 */
export function validateInternalReturnPath(
  raw: string | null | undefined,
): string | null {
  if (raw == null) return null
  let decoded = raw.trim()
  try {
    decoded = decodeURIComponent(decoded)
  } catch {
    return null
  }
  if (!decoded.startsWith("/")) {
    debugLogAuthReturn("reject: not path-relative", { raw })
    return null
  }
  const noHash = decoded.split("#")[0] ?? ""
  const qIdx = noHash.indexOf("?")
  const pathname = (qIdx >= 0 ? noHash.slice(0, qIdx) : noHash).trim()
  const search = qIdx >= 0 ? noHash.slice(qIdx) : ""

  if (!isSafePathname(pathname)) {
    debugLogAuthReturn("reject: pathname", { pathname })
    return null
  }

  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  )

  const tsRaw = params.get("auth_intent_ts")
  let intentExpired = false
  if (tsRaw != null && tsRaw.trim() !== "") {
    if (!validateQueryValue("auth_intent_ts", tsRaw)) {
      intentExpired = true
    }
  }

  const next = new URLSearchParams()
  for (const key of params.keys()) {
    if (!ALLOWED_QUERY_KEYS.has(key)) {
      debugLogAuthReturn("reject: unknown query key", { key })
      return null
    }
    const val = params.get(key)
    if (val == null || val === "") continue
    if (intentExpired && (key === "save_timeline" || key === "resume_upload")) {
      continue
    }
    if (intentExpired && key === "auth_intent_ts") {
      continue
    }
    if (!validateQueryValue(key, val)) {
      debugLogAuthReturn("reject: bad query value", { key })
      return null
    }
    next.set(key, val.trim())
  }

  if (intentExpired) {
    debugLogAuthReturn("sanitized: expired intent", { pathname })
  }

  const qs = next.toString()
  const out = qs ? `${pathname}?${qs}` : pathname
  debugLogAuthReturn("accept", { out })
  return out
}

type StoredAuthReturnPayload = { p: string; t: number }

export function writeSessionAuthReturnPath(validatedPath: string): void {
  if (typeof window === "undefined") return
  try {
    const payload: StoredAuthReturnPayload = {
      p: validatedPath,
      t: Date.now(),
    }
    sessionStorage.setItem(AUTH_RETURN_SESSION_KEY, JSON.stringify(payload))
  } catch {
    /* ignore */
  }
}

export function clearSessionAuthReturnPath(): void {
  if (typeof window === "undefined") return
  try {
    sessionStorage.removeItem(AUTH_RETURN_SESSION_KEY)
  } catch {
    /* ignore */
  }
}

/** Validated path from sessionStorage, or null if missing / expired / invalid. */
export function readSessionAuthReturnPath(): string | null {
  if (typeof window === "undefined") return null
  try {
    const raw = sessionStorage.getItem(AUTH_RETURN_SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<StoredAuthReturnPayload>
    if (typeof parsed.p !== "string" || typeof parsed.t !== "number") {
      sessionStorage.removeItem(AUTH_RETURN_SESSION_KEY)
      return null
    }
    if (Date.now() - parsed.t > SESSION_RETURN_MAX_AGE_MS) {
      sessionStorage.removeItem(AUTH_RETURN_SESSION_KEY)
      debugLogAuthReturn("session return expired")
      return null
    }
    const v = validateInternalReturnPath(parsed.p)
    if (!v) {
      sessionStorage.removeItem(AUTH_RETURN_SESSION_KEY)
    }
    return v
  } catch {
    return null
  }
}

export function buildAuthIntentQuery(
  parts: Record<string, string | undefined>,
): string {
  const params = new URLSearchParams()
  params.set("auth_intent_ts", String(Math.floor(Date.now() / 1000)))
  for (const [k, v] of Object.entries(parts)) {
    if (v != null && v !== "") params.set(k, v)
  }
  return params.toString()
}
